import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  type ReactElement,
  type ReactNode,
} from 'react'
import AlertPopup from '../../components/AlertPopup/AlertPopup'

export interface DialogProps<T = any> {
  close: (result?: T | null) => void
}

export type DialogRenderer<T = any> = (props: DialogProps<T>) => ReactElement

interface AlertDialogState {
  title?: string
  subtitle?: string
  buttonText?: string
  handleClose?: () => void
}

interface DialogContextValue {
  showAlert: (
    title: string,
    subtitle?: string,
    buttonText?: string,
    handleClose?: () => void
  ) => void
  open: <T = any>(dialog: ReactElement | DialogRenderer<T>) => Promise<T | null>
  close: (result?: any) => void
  isOpen: (dialogClass: any) => boolean
  isAnyDialogOpen: boolean
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined)

DialogContext.displayName = 'DialogContext'

export default function useDialog(): DialogContextValue {
  const context = useContext(DialogContext)

  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider')
  }

  return context
}

export function withDialog<P extends { dialog?: DialogContextValue }>(
  Component: React.ComponentType<P>
): React.ComponentType<Omit<P, 'dialog'>> {
  return function WrappedComponent(props: Omit<P, 'dialog'>) {
    return (
      <DialogContext.Consumer>
        {(context) => <Component {...(props as P)} dialog={context} />}
      </DialogContext.Consumer>
    )
  }
}

interface DialogProviderProps {
  children: ReactNode
}

/**
 * DialogProvider manages global dialog state and provides promise-based dialog functionality.
 *
 * @example
 * // Using promise-based dialogs
 * const dialog = useDialog()
 * const result = await dialog.open<'ok' | 'cancel'>(({ open, close }) => (
 *   <Dialog open={open} onClose={() => close('cancel')}>
 *     <button onClick={() => close('ok')}>OK</button>
 *   </Dialog>
 * ))
 * if (result === 'ok') { ... }
 */
export function DialogProvider({ children }: DialogProviderProps) {
  const [alertDialog, setAlertDialog] = useState<AlertDialogState>({})
  const [dialog, setDialog] = useState<ReactElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Promise resolver reference for dialog results
  const resolverRef = useRef<((result: any) => void) | null>(null)

  const showAlertDialog = useCallback(
    (
      title: string,
      subtitle?: string,
      buttonText?: string,
      handleClose?: () => void
    ) => {
      setAlertDialog({
        title,
        subtitle,
        buttonText,
        handleClose,
      })
      setIsOpen(true)
    },
    []
  )

  const closeDialog = useCallback((result?: any) => {
    setAlertDialog({})
    setDialog(null)
    setIsOpen(false)

    // Resolve the promise if there's a pending resolver
    if (resolverRef.current) {
      resolverRef.current(result ?? null)
      resolverRef.current = null
    }
  }, [])

  const showDialog = useCallback(
    <T = any,>(
      dialogOrRenderer: ReactElement | DialogRenderer<T>
    ): Promise<T | null> => {
      return new Promise((resolve) => {
        if (resolverRef.current) {
          resolverRef.current(null)
          resolverRef.current = null
        }

        resolverRef.current = resolve

        // If it's a function (renderer), call it with props
        if (typeof dialogOrRenderer === 'function') {
          const close = (result?: T | null) => {
            closeDialog(result)
          }

          const dialogElement = dialogOrRenderer({ close })
          setDialog(dialogElement)
        } else {
          // It's a ReactElement, use it directly
          setDialog(dialogOrRenderer)
        }
      })
    },
    [closeDialog]
  )

  const dismissDialog = useCallback(() => {
    alertDialog.handleClose?.()
    closeDialog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertDialog.handleClose, closeDialog])

  const isDialogOpen = useCallback(
    (dialogClass: any) => {
      // @ts-ignore
      return dialog?.type?.name === dialogClass?.name
    },
    [dialog]
  )

  const contextValue: DialogContextValue = {
    showAlert: showAlertDialog,
    open: showDialog,
    close: closeDialog,
    isOpen: isDialogOpen,
    isAnyDialogOpen: dialog !== null || isOpen,
  }

  return (
    <DialogContext.Provider value={contextValue}>
      {alertDialog.title && (
        <AlertPopup
          open={isOpen}
          onClick={dismissDialog}
          title={alertDialog.title}
          subtitle={alertDialog.subtitle}
          buttonText={alertDialog.buttonText ?? ''}
        />
      )}
      {dialog}
      {children}
    </DialogContext.Provider>
  )
}
